import { useState, useEffect } from "react";
import { Search, AlertTriangle, Info, Activity, FileText, IndianRupee, Volume2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSpeech } from "../../hooks/useSpeech";

// Types based on the JSON structures
interface ClinicalDisease {
  id: number;
  name: string;
  medicines: string[];
  procedures: string[];
  emergency_flag: boolean;
}

interface CostItem {
  item: string;
  category: string;
  estimated_min: number;
  estimated_max: number;
  unit: string;
  currency: string;
  last_updated: string;
  source: string;
}

export function ClinicalReference() {
  const { t } = useLanguage();
  const { speak, isSpeaking } = useSpeech();
  const [searchTerm, setSearchTerm] = useState("");
  
  const [diseases, setDiseases] = useState<ClinicalDisease[]>([]);
  const [costs, setCosts] = useState<CostItem[]>([]);
  
  useEffect(() => {
    // Dynamic import of the JSON files
    import("../../data/clinical_reference.json").then((module) => {
      setDiseases(module.default as ClinicalDisease[]);
    });
    import("../../data/cost_catalogue.json").then((module) => {
      setCosts(module.default as CostItem[]);
    });
  }, []);

  const filteredDiseases = diseases.filter(d => 
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredCosts = costs.filter(c => 
    c.item.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl pb-10">
      {/* MASSIVE DISCLAIMER */}
      <div className="rounded-2xl border border-amber-500/30 p-6 bg-amber-500/10">
        <div className="flex items-start gap-4">
          <AlertTriangle className="w-8 h-8 text-amber-400 flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-amber-400 font-bold text-lg mb-1">{t("clinical_disclaimer_main")}</h2>
            <p className="text-amber-200/80 text-sm leading-relaxed">
              {t("clinical_disclaimer_sub")} This module provides <strong>estimated reference costs</strong> and <strong>common treatment examples</strong> only. It is NOT a billing system, prescription system, or diagnostic tool. Actual hospital charges and clinical care paths will vary.
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("clinical_search_placeholder")}
          className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 shadow-inner"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DISEASES COLUMN */}
        <div className="space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2 px-1">
            <Activity className="w-4 h-4 text-teal-400" /> 
            Disease Reference Catalogue
          </h3>
          
          <div className="space-y-4">
            {filteredDiseases.slice(0, 15).map(disease => (
              <div key={disease.id} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5 flex justify-between items-start bg-white/[0.02]">
                  <h4 className="text-teal-300 font-medium text-lg">{disease.name}</h4>
                  {disease.emergency_flag && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-red-500/30 bg-red-500/10 text-red-400">
                      <AlertTriangle className="w-3 h-3" />
                      {t("clinical_urgent_review")}
                    </span>
                  )}
                </div>
                
                <div className="p-5 space-y-4">
                  {disease.medicines.length > 0 && (
                    <div>
                      <h5 className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" />
                        {t("clinical_common_treatments")}
                      </h5>
                      <ul className="list-disc pl-5 space-y-1">
                        {disease.medicines.map((med, idx) => (
                          <li key={idx} className="text-gray-300 text-sm">{med}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {disease.procedures.length > 0 && (
                    <div>
                      <h5 className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" />
                        {t("clinical_possible_procedures")}
                      </h5>
                      <ul className="list-disc pl-5 space-y-1">
                        {disease.procedures.map((proc, idx) => (
                          <li key={idx} className="text-gray-300 text-sm">{proc}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {filteredDiseases.length === 0 && (
              <div className="p-6 text-center text-gray-500 border border-white/5 rounded-xl bg-white/5">
                No disease references found.
              </div>
            )}
          </div>
        </div>

        {/* COSTS COLUMN */}
        <div className="space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2 px-1">
            <IndianRupee className="w-4 h-4 text-emerald-400" /> 
            Cost Reference Catalogue
          </h3>
          
          <div className="space-y-4">
            {filteredCosts.slice(0, 15).map((cost, idx) => (
              <div key={idx} className="rounded-xl border border-white/10 bg-white/5 p-5 relative group">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-emerald-300 font-medium text-base">{cost.item}</h4>
                    <span className="text-xs text-gray-500">{cost.category}</span>
                  </div>
                  <button
                    onClick={() => speak(`Estimated reference cost for ${cost.item} is ${cost.estimated_min} to ${cost.estimated_max} ${cost.currency}.`)}
                    className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors border border-emerald-500/20"
                    title={t("btn_speak_cost")}
                  >
                    <Volume2 className={`w-4 h-4 ${isSpeaking ? "animate-pulse" : ""}`} />
                  </button>
                </div>
                
                <div className="bg-black/20 rounded-lg p-3 border border-white/5">
                  <div className="text-xs text-gray-400 mb-1">{t("clinical_cost_estimate")}</div>
                  <div className="text-white font-semibold text-lg">
                    ₹{cost.estimated_min.toLocaleString("en-IN")} – ₹{cost.estimated_max.toLocaleString("en-IN")}
                    <span className="text-xs text-gray-500 font-normal ml-2">/ {cost.unit}</span>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center gap-2 text-[10px] text-gray-500">
                  <Info className="w-3 h-3" />
                  <span>{t("clinical_cost_disclaimer")}</span>
                </div>
              </div>
            ))}
            {filteredCosts.length === 0 && (
              <div className="p-6 text-center text-gray-500 border border-white/5 rounded-xl bg-white/5">
                No cost references found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
